import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ShieldCheck, ArrowLeft, ArrowRight, Mail, MessageCircle,
  CheckCircle2, AlertTriangle, HelpCircle, X, Lock, FileText
} from 'lucide-react';
import Footer from '../components/Footer';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const clauseOk = (c, answers) => {
  const ans = answers[c.q];
  if (ans == null) return false;
  if (Array.isArray(ans)) return ans.some((v) => c.in.includes(v));
  return c.in.includes(ans);
};

export const isVisible = (q, answers) => {
  const cond = q.show_if;
  if (!cond) return true;
  if (cond.all && !cond.all.every((c) => clauseOk(c, answers))) return false;
  if (cond.any && !cond.any.some((c) => clauseOk(c, answers))) return false;
  return true;
};

export const pruneAnswers = (journey, answers) => {
  const eff = {};
  journey.sezioni.forEach((s) =>
    s.domande.forEach((q) => {
      if (isVisible(q, eff) && answers[q.id] != null) eff[q.id] = answers[q.id];
    })
  );
  return eff;
};

const AREA_STYLE = {
  verde: { bg: 'bg-[#0E8C84]/10', border: 'border-[#0E8C84]/30', text: 'text-[#0B7770]', Icon: CheckCircle2 },
  ambra: { bg: 'bg-[#E67E22]/10', border: 'border-[#E67E22]/30', text: 'text-[#c0661a]', Icon: AlertTriangle },
  grigio: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-600', Icon: HelpCircle },
};

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2380]';

const QuestionCard = ({ q, value, onAnswer, answers }) => {
  const toggleMulti = (optId, esclusiva) => {
    let next = Array.isArray(value) ? [...value] : [];
    if (next.includes(optId)) next = next.filter((v) => v !== optId);
    else if (esclusiva) next = [optId];
    else next = [...next.filter((v) => !q.opzioni.find((o) => o.id === v)?.esclusiva), optId];
    onAnswer(q.id, next);
  };
  const noteText = q.note_map ? q.note_map.map[answers[q.note_map.q]] : null;
  return (
    <div data-testid={`risk-question-${q.id}`} className="bg-white rounded-2xl shadow-md p-5 sm:p-6">
      <p className="font-semibold text-gray-800 mb-4 leading-snug">{q.testo}</p>
      <div className="flex flex-wrap gap-2.5">
        {q.opzioni.map((o) => {
          const selected = q.tipo === 'multi' ? Array.isArray(value) && value.includes(o.id) : value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              data-testid={`risk-opt-${q.id}-${o.id}`}
              onClick={() => (q.tipo === 'multi' ? toggleMulti(o.id, o.esclusiva) : onAnswer(q.id, o.id))}
              className={`px-4 py-2.5 rounded-full text-sm font-medium border-2 transition-all duration-200 ${
                selected
                  ? 'bg-[#1B2380] border-[#1B2380] text-white shadow-md'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-[#1B2380]/50'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {noteText && (
        <p data-testid={`risk-note-${q.id}`} className="mt-4 text-xs text-[#1B2380] bg-[#1B2380]/5 border border-[#1B2380]/10 rounded-lg px-3.5 py-2.5 leading-relaxed">
          {noteText}
        </p>
      )}
    </div>
  );
};

const RequestModal = ({ tipo, answers, partnerCode, onClose }) => {
  const [form, setForm] = useState({ nome_ente: '', cf_piva: '', referente: '', email: '', telefono: '', messaggio: '' });
  const [informativa, setInformativa] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const isReport = tipo === 'report_email';

  const cfPivaValid = (v) => {
    const s = v.replace(/\s/g, '').toUpperCase();
    return /^\d{11}$/.test(s) || /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(s);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cfPivaValid(form.cf_piva)) { toast.error('Codice fiscale o Partita IVA non valido: 11 cifre oppure 16 caratteri'); return; }
    if (!informativa) { toast.error("Conferma la presa visione dell'informativa privacy"); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/risk-experience/richiesta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo, ...form,
          consenso_informativa: informativa, consenso_marketing: marketing,
          partner_code: partnerCode || null, answers,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Errore durante l\'invio');
        return;
      }
      setDone(data.message);
    } catch { toast.error('Errore di connessione. Riprova.'); } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="risk-request-modal" className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 sm:p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">
            {isReport ? 'Registrati e ricevi il report completo' : 'Richiedi una consulenza'}
          </h3>
          <button data-testid="risk-modal-close" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {done ? (
          <div data-testid="risk-request-success" className="text-center py-6">
            <CheckCircle2 size={44} className="text-[#0E8C84] mx-auto mb-4" />
            <p className="text-gray-700 font-medium leading-relaxed">{done}</p>
            <button onClick={onClose} className="mt-6 bg-[#1B2380] hover:bg-[#2A3299] text-white font-semibold px-6 py-2.5 rounded-lg transition-colors">Chiudi</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3.5">
            {isReport && (
              <p className="text-sm text-[#1B2380] bg-[#1B2380]/5 border border-[#1B2380]/10 rounded-lg px-3.5 py-2.5 leading-relaxed">
                È un servizio gratuito: per la privacy ci servono i dati dell'ente e i tuoi consensi.
                Il report, su carta intestata Assietica, verrà inviato alla tua email dopo la verifica di un consulente Assietica.
              </p>
            )}
            <input data-testid="risk-form-ente" type="text" required placeholder="Nome dell'ente / associazione *" value={form.nome_ente} onChange={(e) => setForm((p) => ({ ...p, nome_ente: e.target.value }))} className={inputCls} />
            <input data-testid="risk-form-cfpiva" type="text" required placeholder="Codice fiscale o Partita IVA dell'ente *" value={form.cf_piva} onChange={(e) => setForm((p) => ({ ...p, cf_piva: e.target.value }))} className={inputCls} />
            <input data-testid="risk-form-referente" type="text" required placeholder="Nome e cognome del referente *" value={form.referente} onChange={(e) => setForm((p) => ({ ...p, referente: e.target.value }))} className={inputCls} />
            <input data-testid="risk-form-email" type="email" required placeholder="Email *" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className={inputCls} />
            <input data-testid="risk-form-telefono" type="tel" placeholder="Cellulare (facoltativo)" value={form.telefono} onChange={(e) => setForm((p) => ({ ...p, telefono: e.target.value }))} className={inputCls} />
            {!isReport && (
              <textarea data-testid="risk-form-messaggio" rows={3} placeholder="Note o richieste particolari (facoltativo)" value={form.messaggio} onChange={(e) => setForm((p) => ({ ...p, messaggio: e.target.value }))} className={`${inputCls} resize-y`} />
            )}
            <label className="flex items-start gap-2.5 cursor-pointer text-xs text-gray-600 leading-relaxed">
              <input data-testid="risk-form-informativa" type="checkbox" checked={informativa} onChange={(e) => setInformativa(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#1B2380] flex-shrink-0" />
              <span>Dichiaro di aver preso visione dell'<Link to="/privacy-policy" target="_blank" className="text-[#1B2380] underline">Informativa Privacy</Link>. I dati saranno trattati per dare seguito alla mia richiesta. *</span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer text-xs text-gray-600 leading-relaxed">
              <input data-testid="risk-form-marketing" type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#1B2380] flex-shrink-0" />
              <span>Acconsento a ricevere comunicazioni informative e newsletter Assietica (facoltativo).</span>
            </label>
            <button data-testid="risk-form-submit" type="submit" disabled={sending} className="w-full bg-[#1B2380] hover:bg-[#2A3299] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60">
              {sending ? 'Invio in corso...' : isReport ? 'Registrati e richiedi il report' : 'Invia la richiesta di consulenza'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default function RiskExperience() {
  const [searchParams] = useSearchParams();
  const partnerCode = searchParams.get('p') || '';
  const [journey, setJourney] = useState(null);
  const [phase, setPhase] = useState('intro');
  const [sectionIdx, setSectionIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loadingRes, setLoadingRes] = useState(false);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/risk-experience/journey`).then((r) => r.json()).then(setJourney).catch(() => {});
  }, []);

  const effAnswers = useMemo(() => (journey ? pruneAnswers(journey, answers) : {}), [journey, answers]);
  const section = journey?.sezioni[sectionIdx];
  const visibleQuestions = useMemo(
    () => (section ? section.domande.filter((q) => isVisible(q, effAnswers)) : []),
    [section, effAnswers]
  );
  const allAnswered = visibleQuestions.every((q) => {
    const v = answers[q.id];
    return q.tipo === 'multi' ? Array.isArray(v) && v.length > 0 : v != null;
  });

  const onAnswer = useCallback((qid, val) => setAnswers((p) => ({ ...p, [qid]: val })), []);

  const next = async () => {
    if (sectionIdx < journey.sezioni.length - 1) {
      setSectionIdx(sectionIdx + 1);
      window.scrollTo(0, 0);
      return;
    }
    setLoadingRes(true);
    try {
      const res = await fetch(`${API}/api/risk-experience/valuta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: effAnswers }),
      });
      if (!res.ok) throw new Error('valuta failed');
      setResults(await res.json());
      setPhase('results');
      window.scrollTo(0, 0);
    } catch { toast.error('Errore nel calcolo dei risultati. Riprova.'); } finally { setLoadingRes(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Toaster position="top-right" richColors />
      <div className="bg-gradient-to-br from-[#1B2380] via-[#1B2380] to-[#0E8C84] pb-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8">
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium mb-6">
            <ArrowLeft size={15} /> Torna alla home
          </Link>
          <div className="text-center text-white">
            <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-md text-xs font-semibold px-3.5 py-1.5 rounded-full mb-4">
              <ShieldCheck size={13} className="text-[#8FE3DA]" /> Assietica Risk Experience
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold">La mappa orientativa dei rischi del tuo ente</h1>
            <p className="text-white/85 mt-3 max-w-2xl mx-auto text-base">
              Percorso guidato e anonimo per cooperative, APS, ODV, ASD, fondazioni ed enti del Terzo Settore. Nessun punteggio: solo punti da approfondire, spiegati.
            </p>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 -mt-4 pb-16">
        {phase === 'intro' && journey && (
          <div data-testid="risk-intro" className="bg-white rounded-3xl shadow-xl p-7 sm:p-9 mt-2">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Come funziona</h2>
            <ul className="space-y-3 text-gray-600 text-sm leading-relaxed">
              <li className="flex gap-3"><CheckCircle2 size={18} className="text-[#0E8C84] flex-shrink-0 mt-0.5" /> 4 sezioni e circa 30 domande: vedrai solo quelle pertinenti alla tua realtà (cooperativa, APS, ODV, ASD, fondazione...) in circa 7 minuti.</li>
              <li className="flex gap-3"><CheckCircle2 size={18} className="text-[#0E8C84] flex-shrink-0 mt-0.5" /> Ogni domanda tecnica ha l'opzione "Non so": ti diremo dove recuperare l'informazione.</li>
              <li className="flex gap-3"><CheckCircle2 size={18} className="text-[#0E8C84] flex-shrink-0 mt-0.5" /> Alla fine vedi un'anteprima della tua mappa in 6 aree, senza punteggi. Il report completo, su carta intestata Assietica, arriva via email con una registrazione gratuita e la verifica di un consulente.</li>
              <li className="flex gap-3"><Lock size={18} className="text-[#1B2380] flex-shrink-0 mt-0.5" /> Il percorso è anonimo: le risposte non vengono salvate. I tuoi dati vengono chiesti solo se richiedi il report via email o una consulenza.</li>
            </ul>
            <p className="text-gray-500 text-xs mt-6 leading-relaxed border-t border-gray-100 pt-4">{journey.disclaimer}</p>
            <button data-testid="risk-start-btn" onClick={() => setPhase('journey')} className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1B2380] hover:bg-[#2A3299] text-white font-semibold px-8 py-3.5 rounded-xl transition-colors">
              Inizia il percorso <ArrowRight size={16} />
            </button>
          </div>
        )}

        {phase === 'journey' && section && (
          <div data-testid={`risk-section-${section.id}`} className="mt-2">
            <div className="bg-white rounded-2xl shadow-md p-5 mb-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-[#1B2380] uppercase tracking-wide">Sezione {section.id} di {journey.sezioni.length}</p>
                <p className="text-xs text-gray-400">{sectionIdx + 1}/{journey.sezioni.length}</p>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-gradient-to-r from-[#1B2380] to-[#0E8C84] rounded-full transition-all duration-500" style={{ width: `${((sectionIdx + 1) / journey.sezioni.length) * 100}%` }} />
              </div>
              <h2 className="text-lg font-bold text-gray-800">{section.titolo}</h2>
              <p className="text-sm text-gray-500 mt-1">{section.descrizione}</p>
            </div>
            <div className="space-y-4">
              {visibleQuestions.map((q) => (
                <QuestionCard key={q.id} q={q} value={answers[q.id]} onAnswer={onAnswer} answers={effAnswers} />
              ))}
            </div>
            <div className="flex items-center justify-between mt-6">
              <button
                data-testid="risk-back-btn"
                onClick={() => { sectionIdx === 0 ? setPhase('intro') : setSectionIdx(sectionIdx - 1); window.scrollTo(0, 0); }}
                className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                <ArrowLeft size={15} /> Indietro
              </button>
              <button
                data-testid="risk-next-btn"
                onClick={next}
                disabled={!allAnswered || loadingRes}
                className="inline-flex items-center gap-2 bg-[#1B2380] hover:bg-[#2A3299] text-white font-semibold px-7 py-3 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loadingRes ? 'Elaborazione...' : sectionIdx === journey.sezioni.length - 1 ? 'Vedi i risultati' : 'Avanti'} <ArrowRight size={16} />
              </button>
            </div>
            {!allAnswered && <p className="text-xs text-gray-400 text-right mt-2">Rispondi a tutte le domande per proseguire</p>}
          </div>
        )}

        {phase === 'results' && results && (
          <div data-testid="risk-results" className="mt-2 space-y-5">
            <div className="bg-white rounded-3xl shadow-xl p-7">
              <h2 className="text-xl font-bold text-gray-800">La tua anteprima</h2>
              <p className="text-sm text-gray-500 mt-1">
                {results.totale_punti > 0
                  ? `Sono emersi ${results.totale_punti} punti in ${results.n_aree_attenzione} aree, in base alle risposte date. Nessun punteggio: il dettaglio è nel report completo.`
                  : 'Nessun punto di attenzione emerso, in base alle risposte date.'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {results.aree.map((area) => {
                const st = AREA_STYLE[area.etichetta];
                const Icon = st.Icon;
                return (
                  <div key={area.id} data-testid={`risk-area-${area.id}`} className={`rounded-2xl border ${st.border} ${st.bg} p-5`}>
                    <div className="flex items-center gap-2.5 mb-1">
                      <Icon size={18} className={st.text} />
                      <h3 className="font-bold text-gray-800">{area.nome}</h3>
                    </div>
                    <p className={`text-sm font-semibold ${st.text}`}>
                      {area.etichetta_label}
                      {area.etichetta === 'verde' && <span className="font-normal text-gray-500"> — in base alle risposte date</span>}
                    </p>
                    {area.n_punti > 0 && (
                      <p className="mt-2 text-xs text-gray-600 flex items-center gap-1.5">
                        <Lock size={12} className="text-gray-400" />
                        {area.n_punti === 1 ? '1 punto spiegato nel report completo' : `${area.n_punti} punti spiegati nel report completo`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div data-testid="risk-register-cta" className="bg-gradient-to-br from-[#1B2380] to-[#0E8C84] rounded-2xl shadow-lg p-6 sm:p-7 text-white">
              <h3 className="font-bold text-lg flex items-center gap-2"><FileText size={20} className="text-[#8FE3DA]" /> Il report completo è gratuito</h3>
              <p className="text-white/90 text-sm mt-2 leading-relaxed">
                Nel report, su carta intestata Assietica, trovi: la spiegazione di ogni punto emerso, cosa verificare e dove
                (polizze, registri, documenti), gli approfondimenti "Cosa hai scoperto oggi" e la checklist operativa.
                Per inviartelo ci servono i dati dell'ente e i tuoi consensi privacy: è un servizio gratuito e il report
                viene verificato da un consulente Assietica prima dell'invio alla tua email.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-5">
                <button data-testid="risk-email-report" onClick={() => setModal('report_email')} className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-[#1B2380] hover:bg-gray-100 font-semibold py-3 px-4 rounded-xl text-sm transition-colors">
                  <Mail size={16} /> Registrati e ricevi il report completo
                </button>
                <button data-testid="risk-request-consult" onClick={() => setModal('consulenza')} className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-white/60 text-white hover:bg-white/10 font-semibold py-3 px-4 rounded-xl text-sm transition-colors">
                  <MessageCircle size={16} /> Richiedi una consulenza
                </button>
              </div>
            </div>

            <p data-testid="risk-disclaimer" className="text-xs text-gray-500 bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 leading-relaxed">
              {results.disclaimer}
            </p>
          </div>
        )}
      </main>

      {modal && <RequestModal tipo={modal} answers={effAnswers} partnerCode={partnerCode} onClose={() => setModal(null)} />}
      <Footer />
    </div>
  );
}
